import { useMemo, useState } from "react";

import { useFeature } from "~hooks/useFeature";
import {
  type DataAnalyticsTabValue,
  DataAnalyticsTemplate,
} from "~ui/templates/DataAnalyticsTemplate/DataAnalyticsTemplate";

import {
  ApprenticeshipTab,
  CandidateSearchTab,
  OrgGrowthTab,
  SkillsCertificationsTab,
  StudentCandidacyTab,
  TalentAlignmentTab,
  UserGrowthTab,
} from "./AnalyticsTabs";

const STORAGE_KEY = "dataAnalytics.selectedTab";

const getInitialTab = (showResumes: boolean): DataAnalyticsTabValue => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "orgMetrics" || stored === "userMetrics") {
    return stored;
  }
  if (
    (stored === "apprenticeships" ||
      stored === "candidateSearch" ||
      stored === "skillsCertifications" ||
      stored === "studentCandidacy" ||
      stored === "talentAlignment") &&
    showResumes
  ) {
    return stored;
  }
  return "userMetrics";
};

export const DataAnalyticsPage = () => {
  const showResumes = useFeature("showResumes");
  const [selectedTab, setSelectedTab] = useState<DataAnalyticsTabValue>(() => getInitialTab(showResumes));

  const handleTabChange = (newValue: DataAnalyticsTabValue) => {
    setSelectedTab(newValue);
    localStorage.setItem(STORAGE_KEY, newValue);
  };

  const tabs = useMemo(() => {
    const baseTabs: {
      content: JSX.Element;
      label: string;
      value: DataAnalyticsTabValue;
    }[] = [
      { content: <UserGrowthTab />, label: "User Metrics", value: "userMetrics" },
      { content: <OrgGrowthTab />, label: "Organization Metrics", value: "orgMetrics" },
    ];

    if (showResumes) {
      baseTabs.push(
        {
          content: <StudentCandidacyTab />,
          label: "Student Candidacy",
          value: "studentCandidacy",
        },
        {
          content: <ApprenticeshipTab />,
          label: "Apprenticeships",
          value: "apprenticeships",
        },
        {
          content: <CandidateSearchTab />,
          label: "Candidate Search",
          value: "candidateSearch",
        },
      );
      baseTabs.push({
        content: <SkillsCertificationsTab />,
        label: "Skills & Certifications",
        value: "skillsCertifications",
      });
      baseTabs.push({
        content: <TalentAlignmentTab />,
        label: "Talent Alignment",
        value: "talentAlignment",
      });
    }

    return baseTabs;
  }, [showResumes]);

  return <DataAnalyticsTemplate onTabChange={handleTabChange} selectedTab={selectedTab} tabs={tabs} />;
};
